import { useEffect, useState } from "react";
import { Plus, Upload, Link as LinkIcon, X } from "lucide-react";
import { toast } from "sonner";
import VideoCard from "@/components/VideoCard";
import { useAuth } from "@/context/AuthContext";
import { fetchVideos, createEmbedVideo, uploadVideo, deleteVideo, formatApiErrorDetail } from "@/lib/api";

export default function VideoSection({ entityType, entityId, isOwner }) {
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("embed");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [fileObj, setFileObj] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const load = () => fetchVideos({ entity_type: entityType, entity_id: entityId }).then(setVideos).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  const reset = () => {
    setTitle(""); setDescription(""); setSourceUrl(""); setFileObj(null); setProgress(0); setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Please add a title");
    setBusy(true);
    try {
      if (mode === "embed") {
        if (!sourceUrl.trim()) { setBusy(false); return toast.error("Paste a video link"); }
        await createEmbedVideo({ title, description, source_url: sourceUrl, entity_type: entityType, entity_id: entityId });
      } else {
        if (!fileObj) { setBusy(false); return toast.error("Choose a video file"); }
        const fd = new FormData();
        fd.append("file", fileObj);
        fd.append("title", title);
        fd.append("description", description);
        fd.append("entity_type", entityType);
        fd.append("entity_id", entityId);
        await uploadVideo(fd, setProgress);
      }
      toast.success("Video added");
      reset();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add video");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteVideo(id);
      toast.success("Video removed");
      await load();
    } catch {
      toast.error("Could not remove video");
    }
  };

  if (videos.length === 0 && !isOwner) return null;

  const inputCls = "w-full border border-sand-300 px-4 py-3 bg-white focus:border-brand-500 outline-none";

  return (
    <section data-testid="video-section" className="bg-sand-50 border-t border-sand-200 py-20">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between gap-4 mb-10">
          <div>
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-3">Watch</div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-sand-900 leading-none">Videos</h2>
          </div>
          {isOwner && (
            <button
              onClick={() => setOpen((o) => !o)}
              data-testid="add-video-toggle"
              className="inline-flex items-center gap-2 bg-sand-900 text-sand-50 px-5 py-3 font-mono text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-brand-500 transition-colors"
            >
              {open ? <X size={14} /> : <Plus size={14} />} {open ? "Close" : "Add Video"}
            </button>
          )}
        </div>

        {open && isOwner && (
          <form onSubmit={submit} className="border border-sand-200 bg-white p-6 mb-10" data-testid="add-video-form">
            <div className="flex gap-2 mb-5">
              <button type="button" onClick={() => { setMode("embed"); setFileObj(null); }} data-testid="video-mode-embed"
                className={`flex items-center gap-2 px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase border ${mode === "embed" ? "bg-sand-900 text-sand-50 border-sand-900" : "border-sand-300 text-sand-700"}`}>
                <LinkIcon size={13} /> Paste Link
              </button>
              <button type="button" onClick={() => { setMode("upload"); setSourceUrl(""); }} data-testid="video-mode-upload"
                className={`flex items-center gap-2 px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase border ${mode === "upload" ? "bg-sand-900 text-sand-50 border-sand-900" : "border-sand-300 text-sand-700"}`}>
                <Upload size={13} /> Upload File
              </button>
            </div>
            <div className="space-y-4">
              <input data-testid="video-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Video title" className={inputCls} />
              {mode === "embed" ? (
                <input data-testid="video-url-input" value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="YouTube, TikTok, Vimeo or Instagram link" className={inputCls} />
              ) : (
                <input data-testid="video-file-input" type="file" accept="video/*" onChange={(e) => setFileObj(e.target.files?.[0] || null)} className={inputCls} />
              )}
              <textarea data-testid="video-desc-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Description (optional)" className={inputCls} />
              {busy && mode === "upload" && progress > 0 && (
                <div className="h-2 bg-sand-100">
                  <div className="h-full bg-brand-500 transition-all" style={{ width: `${progress}%` }} />
                </div>
              )}
              <button type="submit" disabled={busy} data-testid="video-submit-button"
                className="bg-brand-500 text-sand-50 px-6 py-3 font-mono text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-brand-600 transition-colors disabled:opacity-60">
                {busy ? (mode === "upload" ? `Uploading ${progress}%` : "Adding…") : "Add Video"}
              </button>
            </div>
          </form>
        )}

        {videos.length === 0 ? (
          <p className="font-serif italic text-xl text-sand-600" data-testid="no-videos">No videos yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="videos-grid">
            {videos.map((v, i) => (
              <VideoCard
                key={v.id}
                video={v}
                index={i}
                canDelete={user && (v.owner_user_id === user.user_id || user.role === "admin")}
                onDelete={onDelete}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
